import { ArrowRight } from 'lucide-react'
import { useState } from 'react'
import {
  FadeInOnMount,
  FadeInSection,
} from 'src/shared/components/FadeInSection'
import { Footer } from 'src/shared/components/Footer'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Card } from 'src/shared/components/ui/card'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'

import { ProjectDialog } from './components/ProjectDialog'
import {
  PREVIOUS_WORK_PROJECTS,
  type Testimonial,
  bilingual,
  getProjectTestimonial,
} from './consts'

type Project = (typeof PREVIOUS_WORK_PROJECTS)[number]

/** Columns for the plain grid, so a short last row never leaves one orphan. */
const PLAIN_GRID_COLUMNS: Record<number, string> = {
  1: 'lg:grid-cols-3',
  2: 'lg:grid-cols-2',
  4: 'lg:grid-cols-4',
}

const CARD_BUTTON_CLASS =
  'group h-full w-full rounded-[var(--radius-outer)] text-left transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2'

const ProjectImages = ({
  project,
  className,
}: {
  project: Project
  className?: string
}) => {
  const { t } = useLocalization()

  return (
    <div className={cn('relative w-full shrink-0 bg-muted/40', className)}>
      <Row className="absolute inset-0 items-center justify-center gap-3 p-6">
        {project.images.map((src) => (
          <img
            key={src}
            src={src}
            alt={`${project.customer}, ${t(
              project.product.en,
              project.product.sv
            )}`}
            className="h-full min-w-0 flex-1 object-contain"
            loading="lazy"
          />
        ))}
      </Row>
    </div>
  )
}

const SeeProject = () => {
  const { t } = useLocalization()

  return (
    <span className="inline-flex items-center gap-1 text-md text-slate-500 transition-colors group-hover:text-slate-900">
      {t('See the project', 'Se projektet')}
      <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
    </span>
  )
}

/** A project the customer has spoken about: image beside their words. */
const FeaturedProjectCard = ({
  project,
  testimonial,
  onOpen,
  className,
}: {
  project: Project
  testimonial: Testimonial
  onOpen: () => void
  className?: string
}) => {
  const { t } = useLocalization()

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(CARD_BUTTON_CLASS, className)}
    >
      <Card
        wrapperClassName="h-full"
        className="h-full gap-0 overflow-hidden p-0 lg:flex-row"
      >
        <ProjectImages
          project={project}
          className="aspect-[4/3] lg:aspect-auto lg:min-h-[300px] lg:w-[42%]"
        />

        <Column className="flex-1 justify-between gap-5 p-5 md:p-6">
          <Column className="gap-1">
            <h3 className="text-xl tracking-tight text-slate-900">
              {project.customer}
            </h3>
            <p className="text-md text-slate-500">
              {t(project.product.en, project.product.sv)}
            </p>
          </Column>

          <blockquote className="m-0">
            <p className="line-clamp-4 text-lg leading-relaxed text-slate-700">
              “{t(...bilingual(testimonial.quote))}”
            </p>
          </blockquote>

          <Column className="gap-3">
            <Column className="gap-0.5">
              <span className="text-md font-normal">{testimonial.name}</span>
              {testimonial.title ? (
                <span className="text-md text-slate-500">
                  {t(...bilingual(testimonial.title))}
                </span>
              ) : null}
            </Column>
            <SeeProject />
          </Column>
        </Column>
      </Card>
    </button>
  )
}

const ProjectCard = ({
  project,
  onOpen,
}: {
  project: Project
  onOpen: () => void
}) => {
  const { t } = useLocalization()

  return (
    <button type="button" onClick={onOpen} className={CARD_BUTTON_CLASS}>
      <Card
        wrapperClassName="h-full"
        className="h-full gap-0 overflow-hidden p-0"
      >
        <ProjectImages project={project} className="aspect-[4/3]" />

        <Column className="gap-1 p-4">
          <h3 className="text-xl tracking-tight text-slate-900">
            {project.customer}
          </h3>
          <p className="text-md text-slate-700">
            {t(project.product.en, project.product.sv)}
          </p>
          {project.decoration ? (
            <p className="text-md text-slate-500">
              {t(project.decoration.en, project.decoration.sv)}
            </p>
          ) : null}
          <span className="mt-2">
            <SeeProject />
          </span>
        </Column>
      </Card>
    </button>
  )
}

const featured = PREVIOUS_WORK_PROJECTS.flatMap((project) => {
  const testimonial = getProjectTestimonial(project.id)
  return testimonial ? [{ project, testimonial }] : []
})
const plain = PREVIOUS_WORK_PROJECTS.filter(
  (project) => !getProjectTestimonial(project.id)
)

export const PreviousWorkPage = () => {
  const { t } = useLocalization()
  const [project, setProject] = useState<Project | null>(null)
  const [projectOpen, setProjectOpen] = useState(false)

  const openProject = (next: Project) => {
    setProject(next)
    setProjectOpen(true)
  }

  return (
    <Column className="relative">
      <Column className="relative w-full gap-16 bg-white px-4 pb-16 pt-10 md:gap-24 md:px-6 md:pb-24 md:pt-16">
        <FadeInOnMount>
          <section id="previous-work" className="w-full">
            <Column className="mx-auto w-full max-w-6xl gap-10">
              <Column className="gap-2">
                <span className="ml-0.5 text-md font-normal uppercase tracking-wide">
                  {t('Portfolio', 'Portfolio')}
                </span>

                <h1 className="text-4xl tracking-tight text-slate-900 md:text-5xl">
                  {t('Previous work', 'Tidigare arbete')}
                </h1>

                <p className="text-lg">
                  {t(
                    'A snapshot of campaigns and kits we have shipped for teams and brands.',
                    'Ett urval av kampanjer och kit vi har levererat till team och varumärken.'
                  )}
                </p>
              </Column>

              <Column className="gap-4">
                {featured.length ? (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {featured.map((item, i) => (
                      <FeaturedProjectCard
                        key={item.project.id}
                        project={item.project}
                        testimonial={item.testimonial}
                        onOpen={() => openProject(item.project)}
                        // An odd one out takes the full row
                        className={cn(
                          featured.length % 2 === 1 &&
                            i === featured.length - 1 &&
                            'md:col-span-2'
                        )}
                      />
                    ))}
                  </div>
                ) : null}

                {plain.length ? (
                  <div
                    className={cn(
                      'grid grid-cols-1 gap-4 sm:grid-cols-2',
                      PLAIN_GRID_COLUMNS[plain.length] ?? 'lg:grid-cols-3'
                    )}
                  >
                    {plain.map((item) => (
                      <ProjectCard
                        key={item.id}
                        project={item}
                        onOpen={() => openProject(item)}
                      />
                    ))}
                  </div>
                ) : null}
              </Column>
            </Column>
          </section>
        </FadeInOnMount>
      </Column>

      <ProjectDialog
        project={project}
        open={projectOpen}
        onClose={() => setProjectOpen(false)}
      />

      <FadeInSection>
        <Footer />
      </FadeInSection>
    </Column>
  )
}
